import { useState } from 'react';
import { Send, Check, AlertCircle } from 'lucide-react';

interface TestEmailFormProps {
  onSend: (email: string) => void;
  isSending: boolean;
  success: boolean;
  error?: string;
}

export function TestEmailForm({ onSend, isSending, success, error }: TestEmailFormProps) {
  const [email, setEmail] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (email && !isSending) {
      onSend(email);
    }
  };

  const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  return (
    <div className="border rounded-lg overflow-hidden bg-card">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-4 bg-muted/30 hover:bg-muted/50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Send className="h-5 w-5" />
          <div className="text-left">
            <h3 className="font-semibold">Send Test Email</h3>
            <p className="text-sm text-muted-foreground">
              Send a test email to verify your template
            </p>
          </div>
        </div>
        <div className="text-sm text-muted-foreground">
          {isExpanded ? 'Hide' : 'Show'}
        </div>
      </button>

      {isExpanded && (
        <div className="p-4 border-t">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="test-email" className="block text-sm font-medium">
                Recipient Email Address
              </label>
              <div className="flex gap-2">
                <input
                  id="test-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your.email@example.com"
                  className="flex-1 px-3 py-2 border rounded-md"
                  required
                />
                <button
                  type="submit"
                  disabled={!isValidEmail || isSending}
                  className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  <Send className="h-4 w-4" />
                  {isSending ? 'Sending...' : 'Send Test'}
                </button>
              </div>
              <p className="text-xs text-muted-foreground">
                The test email will be sent with sample data and marked with a [TEST] prefix
              </p>
            </div>

            {/* Success message */}
            {success && (
              <div className="flex items-start gap-2 p-3 bg-success-muted border border-success-border text-success-muted-foreground rounded-lg">
                <Check className="h-5 w-5 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium">Test email sent successfully!</p>
                  <p className="mt-1">
                    Check your inbox at <strong>{email}</strong>. The email should arrive within a few moments.
                  </p>
                </div>
              </div>
            )}

            {/* Error message */}
            {error && (
              <div className="flex items-start gap-2 p-3 bg-error-muted border border-error-border text-error-foreground rounded-lg">
                <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium">Failed to send test email</p>
                  <p className="mt-1">{error}</p>
                </div>
              </div>
            )}

            {/* Rate limit warning */}
            <div className="text-xs text-muted-foreground bg-muted/30 p-3 rounded-lg">
              <strong>Note:</strong> Test emails are rate-limited to 10 per hour per organization.
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
